import { Suspense } from "react";
import type { ReactNode } from "react";
import { Route, Routes, BrowserRouter, Navigate } from "react-router-dom";
import { IntlProvider } from "@/components/app/IntlProvider";
import { useInitializeApp } from "@/hooks/useInitializeApp";
import { sharedEnvironment, QUERY_CLIENT } from "@akina/config";
import {
  NotFoundPage,
  ErrorBoundary,
  LoadingOverlay,
  MaintenanceGuard,
  Toaster,
  useCmsCopyStore,
} from "@akina/presentation";
import { QueryClientProvider } from "@tanstack/react-query";
import { AppRoutes } from "@/infra/router";
import { CheckoutPage } from "../../pages/CheckoutPage";
import { PaymentCompletedPage } from "../../pages/PaymentCompletedPage";
import { ResultPage } from "../../pages/ResultPage";
import { TreatmentSelectionPage } from "../../pages/TreatmentSelectionPage";

export function App() {
  useInitializeApp();

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/treatment-selection" />} />
      <Route path="/treatment-selection" element={<TreatmentSelectionPage />} />
      <Route path="/result" element={<ResultPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path={AppRoutes.PaymentCompleted} element={<PaymentCompletedPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export function AppProviders({ children }: Readonly<{ children: ReactNode }>) {
  const isLoading = useCmsCopyStore(s => s.isLoadingCmsCopy);

  return (
    <ErrorBoundary env={sharedEnvironment.environment}>
      <Suspense fallback={<LoadingOverlay />}>
        <QueryClientProvider client={QUERY_CLIENT}>
          {isLoading ? <LoadingOverlay /> : null}
          <IntlProvider>
            <Toaster />
            <MaintenanceGuard>
              <BrowserRouter>{children}</BrowserRouter>
            </MaintenanceGuard>
          </IntlProvider>
        </QueryClientProvider>
      </Suspense>
    </ErrorBoundary>
  );
}
